import { useCallback, useEffect, useMemo, useState } from 'react'
import { hexFromArgb } from '@material/material-color-utilities'

import { ILogoFileLink } from '@makyoapp/makyo-sdk/api/BrandKits'
import { IBrandKit } from '@makyoapp/makyo-sdk/models/BrandKits'
import { DEFAULT_MATERIAL_TYPOGRAPHY } from '@makyoapp/makyo-tw-ui'

import { IAppClient } from '@/core/app-builder/app/app'
import { UndoableOptions } from '@/core/app-builder/undo'
import constate from '@/lib/constate'
import { queryClient, queryKeys } from '@/lib/react-query'
import { fromSdkBrandKit, toSdkBrandKitBody } from '@/adapters/__project__/BrandKitAdapter'
import {
  BrandKit,
  BrandKitTheme,
  DEFAULT_EXTENDED_COLORS,
  ExtendedColor,
  MultiSourceTheme,
  sourceToHex,
  themeFromMultiSource,
} from '@/shared/brand-kit'
import { MAX_EXTENDED_COLORS } from '@/shared/constants'

interface BrandKitCpColor {
  triggerRef?: React.RefObject<any>
  name: string
  themeName?: string
  target: string
  color: string
  index?: number
  blend?: boolean
  hideBlend?: boolean
}

interface BrandKitViewToken {
  name: string
  theme: MultiSourceTheme
}

const [AppBrandKitProvider, useAppBrandKitContext] = constate(
  'AppBrandKitContext',
  ({ appClient }: { appClient: IAppClient }) => {
    // ========================== States =============================
    // Core State
    const [activeBrandKit, setActiveBrandKit] = useState<BrandKit | undefined>(undefined)
    const [activeBrandKitID, setActiveBrandKitID] = useState<string | undefined>('')
    const [activeColorThemeIndex, setActiveColorThemeIndex] = useState('0')
    const [themes, setThemes] = useState<BrandKitTheme[]>([])

    // Derived State
    const [cpColor, setCpColor] = useState<BrandKitCpColor | undefined>(undefined)
    const [viewToken, setViewToken] = useState<BrandKitViewToken | undefined>(undefined)

    /**
     * Current active theme
     */
    const currentTheme = useMemo(() => {
      return themes[Number(activeColorThemeIndex)]
    }, [themes, activeColorThemeIndex])

    /**
     * Active brandkit active theme
     */
    const brandLogos = useMemo(() => {
      if (!activeBrandKit) return []
      return activeBrandKit.logos.map((item) => ({ value: item, label: activeBrandKit.name ?? '' })) ?? []
    }, [activeBrandKit])

    /**
     * Initial load brandkit function. this is also called when subscriber called
     */
    const handleLoadBrandKit = useCallback((appClient: IAppClient) => {
      if (appClient.builderSettings) {
        setActiveBrandKitID(appClient.builderSettings._Style._BrandKitID)
        setActiveColorThemeIndex(appClient.builderSettings._Style._ColorSchemeID)
      }
      if (appClient.activeBrandKit) {
        let brandkit = fromSdkBrandKit(appClient.activeBrandKit)
        setActiveBrandKit(brandkit)
        setThemes(
          brandkit.colors.map((c) => ({
            name: c.name,
            theme: themeFromMultiSource([c.value], c.customColors ?? []),
          }))
        )
      }
      // if (appClient.brandkits) {
      //   const brandkitList = Array.from(appClient.brandkits.values())
      //   setBrandKits(fromSdkBrandKitPresenterList(brandkitList as IBrandKitPresenter[]))
      // }
    }, [])

    // ========================== Derived =============================

    // Source color
    const brandkitSourceColor = useMemo(() => {
      return currentTheme?.theme.source ? hexFromArgb(currentTheme?.theme.source) : '#000000'
    }, [currentTheme])

    // Extended Color
    const brandkitExtendedColors = useMemo(() => {
      if (!currentTheme) return []

      return currentTheme.theme.customColors.map((c) => {
        return {
          label: c.color.name,
          argb: c.color.value,
          hex: sourceToHex(c.color.value),
        } as ExtendedColor
      })
    }, [currentTheme])

    // Typography
    const brandkitTypography = useMemo(() => {
      return activeBrandKit?.typography || DEFAULT_MATERIAL_TYPOGRAPHY
    }, [activeBrandKit])

    // ========================== Methods =============================
    const handleSetActiveBrandKit = useCallback(
      async (brandKitId: string, colorSchemeId = '0') => {
        if (!brandKitId) return
        await appClient.UpdateBuilderBrandKit({ _BrandKitID: brandKitId, _ColorSchemeID: colorSchemeId })
      },
      [appClient]
    )

    const handleUpdateBrandKit = useCallback(
      async (brandkit: BrandKit, options?: UndoableOptions) => {
        await appClient.UpdateBrandKit(brandkit.id, toSdkBrandKitBody(brandkit), options)

        queryClient.invalidateQueries({
          queryKey: [queryKeys.orgBrandKitDetail, brandkit.id],
        })
      },
      [appClient]
    )

    const handleUpdateActiveBrandKit = useCallback(
      async (callback: (brandkit: IBrandKit) => IBrandKit) => {
        const brandkit = appClient.activeBrandKit
        if (!brandkit) return

        const updatedBrandkit = callback(structuredClone(brandkit))

        await appClient.UpdateBrandKit(brandkit._id, {
          _Name: updatedBrandkit._Name,
          _Colors: updatedBrandkit._Colors,
          _FontsFamily: updatedBrandkit._FontsFamily,
        })

        queryClient.invalidateQueries({
          queryKey: [queryKeys.orgBrandKitDetail, brandkit._id],
        })
      },
      [appClient]
    )

    const uploadLogoBrandkit = useCallback(
      async (brandkitId: string, data: File, fileExtension: string) => {
        return await appClient.UpdateLogoBrandkit(brandkitId, data, fileExtension)
      },
      [appClient]
    )

    const deleteLogoBrandkit = useCallback(
      async (brandkitId: string, data: ILogoFileLink) => {
        return await appClient.DeleteLogoBrandkit(brandkitId, data)
      },
      [appClient]
    )

    // ========================== Effects =============================

    useEffect(() => {
      handleLoadBrandKit(appClient)

      const unsubscribe = appClient.subscribe((type) => {
        if (type === 'brandkit') {
          handleLoadBrandKit(appClient)
        }
      })

      // Cleanup function to close database connection
      return () => {
        unsubscribe()
      }
    }, [appClient, handleLoadBrandKit])

    // make sure extended color length is 12
    useEffect(() => {
      if (!!activeBrandKitID && brandkitExtendedColors.length !== MAX_EXTENDED_COLORS) {
        // replace with default extended colors instead
        handleUpdateActiveBrandKit((prev) => {
          prev._Colors[0]._CustomColors = [...DEFAULT_EXTENDED_COLORS]
          return prev
        })
      }
    }, [activeBrandKitID, brandkitExtendedColors.length, handleUpdateActiveBrandKit])

    return {
      // states
      setActiveBrandKit,
      activeBrandKit,
      activeBrandKitID,
      activeColorThemeIndex,

      // derived
      themes,
      brandLogos,
      setCpColor,
      cpColor,
      currentTheme,
      brandkitSourceColor,
      brandkitExtendedColors,
      brandkitTypography,

      // methods
      handleSetActiveBrandKit,
      handleUpdateBrandKit,
      handleUpdateActiveBrandKit,

      appClient,
      setViewToken,
      viewToken,
      uploadLogoBrandkit,
      deleteLogoBrandkit,
    }
  }
)

export { AppBrandKitProvider, useAppBrandKitContext }
